import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import axios from 'axios';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://api.mbsmartweb.com/api";

const LevelTree = () => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [treeData, setTreeData] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [stats, setStats] = useState({
    totalMembers: 0,
    maxLevel: 0,
    totalInvestment: 0,
    totalCommission: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const UserId = localStorage.getItem("UserId");

  // Fetch level tree data
  const fetchLevelTree = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(`${API_BASE}/leveltree`, { UserId });
      
      if (response.data.success) {
        setTreeData(response.data.data.treeData);
        setStats(response.data.data.stats);
      } else {
        setError(response.data.error || "Failed to load tree data");
      }
    } catch (err) {
      console.error("Error fetching level tree:", err);
      setError(err.response?.data?.error || "Failed to load tree data");
      toast.error("Failed to load team tree");
    } finally {
      setLoading(false);
    }
  };

  // Fetch user details on selection
  const fetchUserDetails = async (userId) => {
    try {
      const response = await axios.post(`${API_BASE}/leveltreeuser`, { UserId: userId });
      
      if (response.data.success) {
        setUserDetails(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching user details:", err);
      toast.error("Failed to load user details");
    }
  };

  useEffect(() => {
    if (UserId) {
      fetchLevelTree();
    }
  }, [UserId]);

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => ({ ...prev, [nodeId]: !prev[nodeId] }));
  };

  const TreeNode = ({ node }) => {
    const isExpanded = expandedNodes[node.id];
    const hasChildren = node.children && node.children.length > 0;

    return (
      <div className="tree-node">
        <div 
          className={`tree-node-content ${selectedUser === node.id ? 'selected' : ''}`}
          onClick={() => {
            setSelectedUser(node.id);
            fetchUserDetails(node.userId);
          }}
        >
          <div className="tree-node-left">
            {hasChildren && (
              <button 
                className="expand-btn" 
                onClick={(e) => { e.stopPropagation(); toggleNode(node.id); }}
              >
                <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`}></i>
              </button>
            )}
            <div className="node-avatar">
              <img src={`https://ui-avatars.com/api/?name=${node.name?.replace(' ', '+')}&background=0f3d2f&color=fff`} alt={node.name} />
            </div>
            <div className="node-info">
              <div className="node-name">
                <strong>{node.name}</strong>
                <span className="node-userid">{node.userId}</span>
              </div>
              <div className="node-details">
                <span className="node-level">
                  <i className="fas fa-chart-line"></i> Level {node.level}
                </span>
                <span className="node-investment">
                  <i className="fas fa-chart-line"></i> {node.investment}
                </span>
                <span className="node-commission">
                  <i className="fas fa-dollar-sign"></i> {node.commission || '$0'}
                </span>
                <span className="node-date">
                  <i className="far fa-calendar-alt"></i> {node.joinDate}
                </span>
              </div>
            </div>
          </div>
        </div>
        {isExpanded && hasChildren && (
          <div className="tree-children">
            {node.children.map(child => (
              <TreeNode key={child.id} node={child} />
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading-container">
          <div className="loader"></div>
          <p>Loading team tree...</p>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="error-container">
          <i className="fas fa-exclamation-circle"></i>
          <p>{error}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <ToastContainer position="top-center" autoClose={3000} />
      <div className="level-tree-page">
        <div className="page-header">
          <div>
            <h1>Team Level Tree</h1>
            <p>Visual representation of your team structure</p>
          </div>
        </div>

        {/* Tree Stats */}
        <div className="tree-stats-container">
          <div className="tree-stat-card">
            <i className="fas fa-users"></i>
            <div>
              <span>Total Members</span>
              <strong>{stats.totalMembers}</strong>
            </div>
          </div>
          <div className="tree-stat-card">
            <i className="fas fa-level-up-alt"></i>
            <div>
              <span>Max Levels</span>
              <strong>{stats.maxLevel}</strong>
            </div>
          </div>
          {/* <div className="tree-stat-card">
            <i className="fas fa-chart-line"></i>
            <div>
              <span>Total Investment</span>
              <strong>${stats.totalInvestment.toLocaleString()}</strong>
            </div>
          </div>
          <div className="tree-stat-card">
            <i className="fas fa-dollar-sign"></i>
            <div>
              <span>Total Commission</span>
              <strong>${stats.totalCommission?.toLocaleString() || '0'}</strong>
            </div>
          </div> */}
        </div>

        {/* Tree View */}
        <div className="tree-view-container">
          <div className="tree-header">
            <div className="tree-header-item">
              <i className="fas fa-user"></i> Member
            </div>
            <div className="tree-header-item">
              <i className="fas fa-chart-line"></i> Level
            </div>
            <div className="tree-header-item">
              <i className="fas fa-chart-line"></i> Investment
            </div>
            <div className="tree-header-item">
              <i className="fas fa-dollar-sign"></i> Commission
            </div>
            <div className="tree-header-item">
              <i className="far fa-calendar-alt"></i> Join Date
            </div>
          </div>
          <div className="tree-view">
            {treeData && <TreeNode node={treeData} />}
          </div>
        </div>

        {/* User Details Panel */}
        {userDetails && (
          <div className="user-details-panel">
            <div className="panel-header">
              <h3><i className="fas fa-user-circle"></i> Member Details</h3>
              <button className="close-panel" onClick={() => {
                setSelectedUser(null);
                setUserDetails(null);
              }}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="panel-content">
              <div className="detail-row">
                <span className="detail-label"><i className="fas fa-user"></i> Name:</span>
                <span className="detail-value">{userDetails.name}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label"><i className="fas fa-id-card"></i> User ID:</span>
                <span className="detail-value">{userDetails.userId}</span>
              </div>
             {/* <div className="detail-row">
                <span className="detail-label"><i className="fas fa-chart-line"></i> Level:</span>
                <span className="detail-value">Level {userDetails.level}</span>
              </div>*/}
              {/* <div className="detail-row">
                <span className="detail-label"><i className="fas fa-phone"></i> Mobile:</span>
                <span className="detail-value">{userDetails.mobile}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label"><i className="fas fa-envelope"></i> Email:</span>
                <span className="detail-value">{userDetails.email}</span>
              </div> */}
              <div className="detail-row">
                <span className="detail-label"><i className="fas fa-user-plus"></i> Sponsor ID:</span>
                <span className="detail-value">{userDetails.sponsorId}</span>
              </div>
              {/* <div className="detail-row">
                <span className="detail-label"><i className="fas fa-chart-line"></i> Investment:</span>
                <span className="detail-value">{userDetails.investment}</span>
              </div> */}
              <div className="detail-row">
                <span className="detail-label"><i className="fas fa-dollar-sign"></i> Rank:</span>
                <span className="detail-value">{userDetails.RankName  }</span>
              </div>
              <div className="detail-row">
                <span className="detail-label"><i className="fas fa-users"></i> Direct Team:</span>
                <span className="detail-value">{userDetails.directCount}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label"><i className="fas fa-users"></i> Total Team:</span>
                <span className="detail-value">{userDetails.totalTeam}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label"><i className="far fa-calendar-alt"></i> Join Date:</span>
                <span className="detail-value">{userDetails.joinDate}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label"><i className="fas fa-circle"></i> Status:</span>
                <span className={`status-badge ${userDetails.status.toLowerCase()}`}>
                  <i className={`fas ${userDetails.status.toLowerCase() === 'active' ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
                  {userDetails.status}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default LevelTree;